import { EstimateItem, GlassType } from '../context/GlassContext';
import { calculateAreaSqFt, fractionToDecimal } from './measurementUtils';
import { useMemo } from 'react';
import { GlassTypeCache } from '../services/GlassTypeCache';
import { useEstimate } from '../contexts/EstimateContext';

/**
 * Safely extracts a glass type ID from an EstimateItem's glassType property
 * @param glassType The glassType property from an EstimateItem (can be string or GlassType object)
 * @returns The extracted ID or null if invalid
 */
const extractGlassTypeId = (glassType: string | GlassType | null | undefined): string | null => {
  if (!glassType) return null;

  if (typeof glassType === 'string') {
    return glassType.trim() || null;
  }

  if (typeof glassType === 'object' && glassType !== null && typeof glassType.id === 'string') {
    return glassType.id.trim() || null;
  }

  return null;
};

/**
 * Safely extracts a glass type name from an EstimateItem's glassType property
 * @param glassType The glassType property from an EstimateItem (can be string or GlassType object)
 * @returns The extracted name or null if invalid
 */
const extractGlassTypeName = (glassType: string | GlassType | null | undefined): string | null => {
  if (!glassType) return null;

  if (typeof glassType === 'object' && glassType !== null && typeof glassType.name === 'string') {
    return glassType.name.trim() || null;
  }

  // For string values, we assume it's an ID, not a name
  return null;
};

export const DEFAULT_RATES = {
  SHIPPING_PERCENTAGE: 15,
  SURCHARGE_PERCENTAGE: 0,
  TAX_PERCENTAGE: 8.25,
} as const;

export const FORMATTING_OPTIONS = {
  currency: {
    locale: 'en-US',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
  weight: {
    unit: 'lbs',
    precision: 2,
  },
  area: {
    unit: 'sq ft',
    precision: 2,
  },
} as const;

/**
 * Rounds a dimension up to the next even number
 * @param dimension The dimension to round (in inches)
 * @returns The rounded dimension (in inches)
 */
const roundUpToEven = (dimension: number): number => {
  // If the dimension is already even, return it as is
  if (dimension % 2 === 0) {
    return dimension;
  }

  // Otherwise, round up to the next even number
  return Math.ceil(dimension / 2) * 2;
};

/**
 * Gets a default glass type for missing references
 * @param id The ID of the missing glass type
 * @param thickness The thickness of the glass (if known)
 * @param glassTypes Array of all available glass types to find a similar match
 * @param originalGlassType Optional original glass type object to use for properties
 * @returns A default glass type with the given ID
 */
const getDefaultGlassType = (id: string, thickness: string, glassTypes: GlassType[], originalGlassType?: Partial<GlassType> | null): GlassType => {
  // Use the GlassTypeCache service to get or restore the glass type
  return GlassTypeCache.getOrRestoreGlassType(id, glassTypes, thickness, originalGlassType);
};

/**
 * Validates an estimate item and its associated glass type
 * @param item The estimate item to validate
 * @param glassType The glass type associated with the item
 * @throws {Error} If validation fails
 */
const validateEstimateItem = (item: EstimateItem, glassType: GlassType | undefined): void => {
  if (!item) throw new Error('Invalid estimate item');

  // Glass type validation is now handled in calculateItemCost
  // Instead of throwing an error, we'll use a default glass type
  if (!glassType) {
    console.warn('Glass type not found, using default glass type');
    return;
  }

  if (item.quantity <= 0) throw new Error('Quantity must be greater than 0');

  try {
    // Validate dimensions - handle both object and number formats
    if (typeof item.width === 'object' && item.width !== null && typeof item.height === 'object' && item.height !== null) {
      // Handle object format (with feet, inches, fraction)
      const widthInches = (item.width.inches || 0) + fractionToDecimal(item.width.fraction || '0');
      const heightInches = (item.height.inches || 0) + fractionToDecimal(item.height.fraction || '0');

      if (widthInches <= 0 && (item.width.feet || 0) === 0) {
        throw new Error('Width must be greater than 0');
      }

      if (heightInches <= 0 && (item.height.feet || 0) === 0) {
        throw new Error('Height must be greater than 0');
      }
    } else if (typeof item.width === 'number' && typeof item.height === 'number') {
      // Handle number format (direct dimensions)
      if (item.width <= 0) {
        throw new Error('Width must be greater than 0');
      }

      if (item.height <= 0) {
        throw new Error('Height must be greater than 0');
      }
    } else if (item.adjustedWidth && item.adjustedHeight) {
      // Handle adjusted dimensions (for some estimate formats)
      if (item.adjustedWidth <= 0 || item.adjustedHeight <= 0) {
        throw new Error('Adjusted dimensions must be greater than 0');
      }
    } else {
      console.warn('Invalid dimension format in item:', item);
      // Don't throw error, just log warning to prevent breaking the UI
    }
  } catch (error) {
    console.warn('Error validating dimensions:', error);
    // Don't re-throw to prevent breaking the UI
  }
};

/**
 * Calculates the cost, weight, and area for a single estimate item
 * @param item The estimate item to calculate for
 * @param glassTypes Available glass types
 * @param roundUpGlassSize Whether to round up glass dimensions to the next even number for pricing
 * @returns Object containing cost, weight, and area calculations
 * @throws {Error} If validation fails
 */
export const calculateItemCost = (
  item: EstimateItem,
  glassTypes: GlassType[],
  roundUpGlassSize: boolean = false
): { cost: number; weight: number; area: number } => {
  // Default return values for error cases
  const defaultReturn = { cost: 0, weight: 0, area: 0 };

  // Check if item or glassTypes are undefined/null
  if (!item || !glassTypes || !Array.isArray(glassTypes)) {
    return defaultReturn;
  }

  try {
    // Extract glass type ID safely
    const glassTypeId = extractGlassTypeId(item.glassType);

    // If no valid glass type ID, return default values
    if (!glassTypeId) {
      console.warn('No valid glass type ID found in item:', item);
      return defaultReturn;
    }

    // Use the GlassTypeCache service to get or restore the glass type
    let glassType: GlassType;

    // If the glass type is an object with all required properties, use it directly
    if (typeof item.glassType === 'object' &&
        item.glassType !== null &&
        (typeof (item.glassType as any).costPerSqFt === 'number' || typeof (item.glassType as any).price === 'number') &&
        (typeof (item.glassType as any).weightPerSqFt === 'number' || typeof (item.glassType as any).lbsPerSqFt === 'number')
    ) {
      console.log('Using glass type object directly:', item.glassType);
      glassType = {
        ...item.glassType as GlassType,
        id: glassTypeId,
        isRestored: true
      };

      // Add to cache for future use
      GlassTypeCache.addToCache(glassType);
    } else {
      // Try to find by name first if available
      const glassTypeName = extractGlassTypeName(item.glassType);
      if (glassTypeName) {
        const matchByName = glassTypes.find(type =>
          type.name.toLowerCase().trim() === glassTypeName.toLowerCase().trim()
        );

        if (matchByName) {
          console.log(`Found glass type by name match: ${glassTypeName}`);
          glassType = {
            ...matchByName,
            id: glassTypeId,
            isRestored: true
          };
          GlassTypeCache.addToCache(glassType);
        } else {
          // Use the cache service to get or restore the glass type
          // Pass the original glass type object if available
          const originalGlassType = typeof item.glassType === 'object' ? item.glassType : null;
          glassType = GlassTypeCache.getOrRestoreGlassType(glassTypeId, glassTypes, item.thickness, originalGlassType);
        }
      } else {
        // Use the cache service to get or restore the glass type
        // Pass the original glass type object if available
        const originalGlassType = typeof item.glassType === 'object' ? item.glassType : null;
        glassType = GlassTypeCache.getOrRestoreGlassType(glassTypeId, glassTypes, item.thickness, originalGlassType);
      }
    }

    // At this point, we should have a valid glass type (either found or created)
    // Calculate the area for a single piece
    const singlePieceArea = calculateArea(item, false);

    return {
      cost: calculateCost(item, glassType, roundUpGlassSize),
      weight: calculateWeight(item, glassType, roundUpGlassSize),
      // Note: We don't use roundUpGlassSize for the area calculation that's displayed to the user
      // Multiply by quantity to get the total area for all pieces of this item
      area: singlePieceArea * item.quantity
    };
  } catch (error) {
    console.error('Error in calculateItemCost:', error);
    return defaultReturn;
  }

  // This code is no longer needed as we've completely rewritten the calculateItemCost function above
};

/**
 * Calculates totals for all estimate items
 * @param items Array of estimate items
 * @param glassTypes Available glass types
 * @param rates Optional rates for shipping, surcharge, and tax
 * @param roundUpGlassSize Whether to round up glass dimensions to the next even number for pricing
 * @returns Object containing subtotal, shipping, surcharge, tax, total, weight, and area
 */
export const calculateTotals = (
  items: EstimateItem[],
  glassTypes: GlassType[],
  rates: {
    shippingPercentage?: number;
    surchargePercentage?: number;
    taxPercentage?: number;
  } = {},
  roundUpGlassSize: boolean = false
): {
  subtotal: number;
  shipping: number;
  surcharge: number;
  tax: number;
  total: number;
  weight: number;
  area: number;
} => {
  // Initialize totals
  let subtotal = 0;
  let weight = 0;
  let area = 0;

  // Apply default rates if not provided
  const shippingPercentage = rates.shippingPercentage ?? DEFAULT_RATES.SHIPPING_PERCENTAGE;
  const surchargePercentage = rates.surchargePercentage ?? DEFAULT_RATES.SURCHARGE_PERCENTAGE;
  const taxPercentage = rates.taxPercentage ?? DEFAULT_RATES.TAX_PERCENTAGE;

  // Calculate subtotal, weight, and area
  items.forEach(item => {
    // Skip validation messages for incomplete items
    if (!item.glassType) {
      // Skip silently instead of logging to console
      return;
    }

    const { cost, weight: itemWeight, area: itemArea } = calculateItemCost(item, glassTypes, roundUpGlassSize);

    subtotal += cost;
    weight += itemWeight;
    area += itemArea;
  });

  // Calculate shipping, surcharge, tax, and total
  const shipping = subtotal * (shippingPercentage / 100);
  const surcharge = subtotal * (surchargePercentage / 100);
  const tax = (subtotal + shipping + surcharge) * (taxPercentage / 100);
  const total = subtotal + shipping + surcharge + tax;

  return {
    subtotal,
    shipping,
    surcharge,
    tax,
    total,
    weight,
    area,
  };
};

/**
 * Custom hook to calculate totals with memoization
 */
export const useCalculateTotals = (
  items: EstimateItem[],
  glassTypes: GlassType[],
  rates: {
    shippingPercentage?: number;
    surchargePercentage?: number;
    taxPercentage?: number;
    roundUpGlassSize?: boolean;
  } = {}
) => {
  // Get the roundUpGlassSize setting from the context or from the rates parameter
  const { settings } = useEstimate();
  const contextRoundUpGlassSize = settings?.roundUpGlassSize || false;

  // Use the roundUpGlassSize from rates if provided, otherwise use the context value
  const roundUpGlassSize = rates.roundUpGlassSize !== undefined ? rates.roundUpGlassSize : contextRoundUpGlassSize;

  return useMemo(() => calculateTotals(items, glassTypes, rates, roundUpGlassSize),
    [items, glassTypes, rates, roundUpGlassSize]);
};

/**
 * Formats a number as currency using the specified locale and options
 */
export const formatCurrency = (amount: number, options = FORMATTING_OPTIONS.currency): string => {
  try {
    if (typeof amount !== 'number' || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat(options.locale, {
      style: 'currency',
      currency: options.currency,
      minimumFractionDigits: options.minimumFractionDigits,
      maximumFractionDigits: options.maximumFractionDigits,
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return '$0.00';
  }
};

/**
 * Formats a number as weight with the specified unit and precision
 */
export const formatWeight = (weight: number, options = FORMATTING_OPTIONS.weight): string => {
  try {
    if (typeof weight !== 'number' || isNaN(weight)) return `0 ${options.unit}`;
    return `${weight.toFixed(options.precision)} ${options.unit}`;
  } catch (error) {
    console.error('Error formatting weight:', error);
    return `0 ${options.unit}`;
  }
};

/**
 * Formats a number as area with the specified unit and precision
 */
export const formatArea = (area: number, options = FORMATTING_OPTIONS.area): string => {
  try {
    if (typeof area !== 'number' || isNaN(area)) return `0 ${options.unit}`;
    return `${area.toFixed(options.precision)} ${options.unit}`;
  } catch (error) {
    console.error('Error formatting area:', error);
    return `0 ${options.unit}`;
  }
};

/**
 * Helper function to calculate area for an estimate item
 * @param item The estimate item
 * @param roundUpGlassSize Whether to round up glass dimensions to the next even number for pricing
 * @returns The area in square feet
 */
const calculateArea = (item: EstimateItem, roundUpGlassSize: boolean = false): number => {
  try {
    // Handle adjusted dimensions if available (some estimate formats use these)
    if (typeof item.adjustedWidth === 'number' && typeof item.adjustedHeight === 'number' &&
        item.adjustedWidth > 0 && item.adjustedHeight > 0) {
      // Apply rounding if enabled
      let width = item.adjustedWidth;
      let height = item.adjustedHeight;

      if (roundUpGlassSize) {
        width = roundUpToEven(width);
        height = roundUpToEven(height);
      }

      // Convert from inches to feet
      const widthFeet = width / 12;
      const heightFeet = height / 12;
      return widthFeet * heightFeet;
    }

    // Use standard dimensions
    if (roundUpGlassSize && typeof item.width === 'number' && typeof item.height === 'number') {
      // For numeric dimensions, round up if enabled
      const roundedWidth = roundUpToEven(item.width);
      const roundedHeight = roundUpToEven(item.height);
      return calculateAreaSqFt(roundedWidth, roundedHeight);
    }

    return calculateAreaSqFt(item.width, item.height);
  } catch (error) {
    console.error('Error calculating area:', error);
    return 0;
  }
};

/**
 * Helper function to calculate cost for an estimate item
 * @param item The estimate item
 * @param glassType The glass type
 * @param roundUpGlassSize Whether to round up glass dimensions to the next even number for pricing
 * @returns The cost
 */
const calculateCost = (item: EstimateItem, glassType: GlassType | any, roundUpGlassSize: boolean = false): number => {
  try {
    const area = calculateArea(item, roundUpGlassSize);
    // Handle different property names for cost (costPerSqFt or price)
    let costPerUnit = glassType.costPerSqFt || glassType.price || 0;

    // Add cost for insulated glass options if applicable
    if (item.hasArgonGas) {
      // Add $2.00 per square foot for argon gas
      costPerUnit += 2.00;
    }

    if (item.hasAluminumSpacer) {
      // Add $0.75 per square foot for aluminum spacer
      costPerUnit += 0.75;
    }

    return area * costPerUnit * item.quantity;
  } catch (error) {
    console.error('Error calculating cost:', error);
    return 0;
  }
};

/**
 * Helper function to calculate weight for an estimate item
 * @param item The estimate item
 * @param glassType The glass type
 * @param roundUpGlassSize Whether to round up glass dimensions to the next even number for pricing
 * @returns The weight
 */
const calculateWeight = (item: EstimateItem, glassType: GlassType | any, roundUpGlassSize: boolean = false): number => {
  try {
    // Note: We use the same rounding logic for weight as for cost to keep them consistent
    const area = calculateArea(item, roundUpGlassSize);
    // Handle different property names for weight (weightPerSqFt or lbsPerSqFt)
    const weightPerUnit = glassType.weightPerSqFt || glassType.lbsPerSqFt || 0;
    return area * weightPerUnit * item.quantity;
  } catch (error) {
    console.error('Error calculating weight:', error);
    return 0;
  }
};
